import React from 'react'
import styled from '@emotion/styled'
import { HiCheck } from 'react-icons/hi'
import { StepStatus } from 'hooks/useStep'

interface SectionProps {
  gridArea?: string
  canCreate?: boolean
  caption?: boolean
  hidden?: boolean
  title?: React.ReactNode
  description?: string | React.ReactNode
  stepStatus?: StepStatus
  stepNumber?: number
  children?: React.ReactNode
}

export const Section: React.FC<SectionProps> = ({
  gridArea,
  canCreate = true,
  caption = false,
  hidden = false,
  title,
  description,
  stepStatus,
  stepNumber,
  children
}) => {
  const showStepIndicator = stepStatus && stepNumber !== undefined && !!title

  return (
    <Card
      gridArea={gridArea}
      caption={caption}
      hidden={hidden}
      canCreate={canCreate}
    >
      <SectionHeader>
        {showStepIndicator && (
          <Step status={stepStatus}>
            {stepStatus === StepStatus.completed ? <HiCheck /> : stepNumber}
          </Step>
        )}
        <Content>
          {title && <strong>{title}</strong>}
          {description && <span>{description}</span>}
        </Content>
      </SectionHeader>
      {children}
    </Card>
  )
}

export const Card = styled.div<{
  gridArea?: string
  caption?: boolean
  hidden?: boolean
  canCreate?: boolean
}>(
  ({ gridArea, caption = false, hidden = false, canCreate = true }) => `
  ${gridArea && `grid-area: ${gridArea}/${gridArea};`}
  pointer-events: ${canCreate ? 'all' : 'none'};
  
  display: ${hidden ? 'none' : 'flex'};
  flex-flow: column;
  gap: 24px;
  
  background-color: #ffffff;
  border-radius: 8px;
  padding: 24px;
  max-width: 612px;

  ${
    caption &&
    `
      gap: 10px;
      height: min-content;
      max-width: 360px;
    `
  }
`
)

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const Content = styled.div`
  display: flex;
  flex-flow: column;

  line-height: 1.5;

  strong {
    font-size: 16px;
    font-weight: 600;
  }

  span {
    font-size: 14px;
    font-weight: 400;
  }

  a {
    text-decoration: underline;
    font-weight: 500;
  }
`

const Step = styled.div<{ status: StepStatus }>`
  ${({ status }) => `
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    min-width: 28px;

    line-height: 18px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: #D092DD;
    border-radius: 100%;
    border: 2px solid #D092DD;

    ${
      status === StepStatus.inactive &&
      `
      background: #F3F2F4;
      border: 2px solid #E7E5E9;
      color: #8C8CA1;
    `
    }

    ${
      status === StepStatus.active &&
      `
      border: 2px solid #D092DD;
      box-shadow: 0px 0px 0px 4px #D092DD33;
    `
    }

    ${
      status === StepStatus.completed &&
      `
      border: 2px solid #D092DD;
      background: #D092DD;

      svg {
        fill: #ffffff;
      }
    `
    }
  `}
`
